import React, { useRef, useState } from 'react';
import { Upload, message, Button, Form, Input, Spin, Card, Divider } from 'antd';
import { FolderOpenOutlined, DeleteOutlined, UploadOutlined, ArrowUpOutlined, ArrowDownOutlined, StarOutlined } from '@ant-design/icons';
import api, { uploadArchive, updateImageMeta, getImageThumbnail } from '../services/api';
import { Tag, Alert } from 'antd';
import { useQuickTags } from '../utils/useQuickTags';
// 移除全局快速标签与信息

const { Dragger } = Upload;

type PendingItem = {
    uid: string;
    file: File;
    preview: string;
    tags: string;
    category: string;
};


const MobileUploadPage: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<PendingItem[]>([]);
    const [archiveUploading, setArchiveUploading] = useState<boolean>(false);
    const [archiveResults, setArchiveResults] = useState<Array<{ id: number; filename: string; url: string; description: string; tags: string[] }>>([]);
    // 移除压缩包导入展示
    const dirInputRef = useRef<HTMLInputElement>(null);
    // 移除压缩包输入
    // 全局描述/标签（模仿PC端，仅在点击“应用到全部”时覆盖）
    const [globalBarDescription, setGlobalBarDescription] = useState('');
    const [managedTags, setManagedTags] = useState<string[]>([]);
    const [newTag, setNewTag] = useState('');
    const [quickTags, quickActions] = useQuickTags();

    // 移除：全局组合标签逻辑

    // 移除：全局自动填充逻辑

    // 移除：全局应用按钮逻辑

    // 移除：顶部快捷条应用逻辑


    const onFinish = async () => {
        setLoading(true);
        // 以卡片中已汇总的 items 为准
        const pending: PendingItem[] = items.slice();

        // 允许仅提交解压结果的元信息
        if (!pending.length && archiveResults.length === 0) {
            message.warning('请先选择图片或上传压缩包');
            setLoading(false);
            return;
        }

        // 放宽校验：描述与标签均为可选

        const engine = 'baidu_cloud';
        let ok = 0, fail = 0;
        try {
            for (const it of pending) {
                const fd = new FormData();
                fd.append('file', it.file);
                // 仅使用当前卡片的描述与标签
                const manualTags = (it.tags || '').split(',').map(s => s.trim()).filter(Boolean);
                const allTags = manualTags;
                const uniqTags: string[] = [];
                const seen = new Set<string>();
                allTags.forEach(t => { const v = (t||'').trim(); if (v && !seen.has(v)) { seen.add(v); uniqTags.push(v); }});
                const finalDesc = (it.category || '').trim();
                if (finalDesc) fd.append('description', finalDesc);
                fd.append('engine', engine);
                if (uniqTags.length) fd.append('tags', uniqTags.join(','));
                // 计算 checksum（SHA-256）用于后端一致性校验
                try {
                    const buf = await it.file.arrayBuffer();
                    const hash = await crypto.subtle.digest('SHA-256', buf);
                    const hex = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
                    fd.append('checksum', hex);
                } catch {}
                await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
                ok += 1;
            }
            // 批量更新解压结果的描述/标签（仅提交用户填写的项）
            let updateOk = 0;
            for (const it of archiveResults) {
                const hasDesc = !!(it.description && it.description.trim());
                const hasTags = Array.isArray(it.tags) && it.tags.length > 0;
                if (!hasDesc && !hasTags) continue;
                await updateImageMeta(it.id, {
                    description: hasDesc ? it.description : undefined,
                    tags: hasTags ? it.tags : undefined,
                });
                updateOk += 1;
            }
            if (ok) message.success(`上传成功 ${ok} 张${fail ? `，失败 ${fail} 张` : ''}`);
            if (!ok && archiveResults.length > 0) {
                message.success(`已提交 ${archiveResults.length} 张解压图片的元信息${updateOk ? `，更新 ${updateOk} 张` : ''}`);
            }
            if (fail && !ok) message.error('上传失败，请重试');
            form.resetFields();
            // 清空待上传列表
            try { items.forEach(i => URL.revokeObjectURL(i.preview)); } catch {}
            setItems([]);
            // 不清空解压结果，以便继续编辑；如需清空可根据需要重置
        } catch (error: any) {
            fail += 1;
            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;
            if (status === 401) {
                message.error('未登录或登录已过期，请先登录');
                window.location.href = '/matching/login';
            } else {
                message.error(detail || '图片上传失败，请重试。');
            }
        } finally {
            setLoading(false);
        }
    };

    const normFile = (e: any) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e && e.fileList;
    };

    const onDraggerChange = (info: any) => {
        const list = (info?.fileList || []) as any[];
        const next: PendingItem[] = [];
        list.forEach((f: any) => {
            const of: File | undefined = f.originFileObj;
            if (of) {
                // 默认描述：若存在 webkitRelativePath，则取其第一层文件夹名；否则留空待用户填写
                let defaultDesc = '';
                const rel = (of as any).webkitRelativePath || '';
                if (rel && rel.includes('/')) defaultDesc = rel.split('/')[0];
                next.push({ uid: f.uid || `${of.name}-${of.size}-${of.lastModified}`, file: of, preview: f.thumbUrl || URL.createObjectURL(of), tags: '', category: defaultDesc });
            }
        });
        setItems(next);
    };

    const removeItem = (uid: string) => {
        setItems(prev => {
            const found = prev.find(i => i.uid === uid);
            if (found) {
                try { URL.revokeObjectURL(found.preview); } catch {}
            }
            return prev.filter(i => i.uid !== uid);
        });
    };

    // 压缩包上传按钮
    const beforeUploadArchive = async (file: File) => {
        try {
            const ext = (file.name.split('.').pop() || '').toLowerCase();
            if (!['zip', 'rar'].includes(ext)) {
                message.error('仅支持 .zip 或 .rar 文件');
                return Upload.LIST_IGNORE as any;
            }
            setArchiveUploading(true);
            const resp = await uploadArchive(file);
            const list = Array.isArray((resp as any)?.results) ? (resp as any).results : [];
            const count = Number((resp as any)?.success_count ?? (resp as any)?.count ?? list.length ?? 0);
            if (count > 0) {
                message.success(`已导入 ${count} 张图片`);
                // 归一化结果并展示为两列可编辑网格
                const norm = list.map((it: any) => {
                    const id = Number(it?.image_id || it?.id || 0);
                    const filename = String(it?.filename || '');
                    const file_path = String(it?.file_path || '');
                    const url = file_path.startsWith('/') ? file_path : `/${file_path}`;
                    return { id, filename, url, description: '', tags: [] as string[] };
                }).filter((x: any) => x.id > 0);
                setArchiveResults(norm);
            } else {
                message.warning('压缩包中未找到可用图片');
                setArchiveResults([]);
            }
        } catch (e: any) {
            const msg = e?.response?.data?.detail || '压缩包上传失败';
            message.error(msg);
        } finally {
            setArchiveUploading(false);
        }
        return Upload.LIST_IGNORE as any;
    };

    // 已移除逐卡保存，统一由“开始上传”提交

    // 应用全局描述/标签到全部卡片
    const applyGlobalBarToAll = () => {
        if (items.length === 0) {
            message.warning('请先选择图片');
            return;
        }
        const desc = (globalBarDescription || '').trim();
        const tagsArr = managedTags;
        if (!desc && tagsArr.length === 0) {
            message.warning('请填写全局描述或标签');
            return;
        }
        setItems(prev => prev.map(x => ({
            ...x,
            category: desc || x.category,
            tags: tagsArr.length ? tagsArr.join(',') : x.tags,
        })));
        message.success('已应用到全部图片');
    };

    return (
        <div style={{ padding: '12px', maxWidth: 360, margin: '0 auto' }}>
            <Spin spinning={loading}>
            <Alert type="info" showIcon style={{ marginBottom: 12 }} message={
              <span>当前识别引擎：<Tag color="blue">百度云 (baidu_cloud)</Tag>（上传将同步至百度相似图库）</span>
            } />
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="dragger"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                        style={{ marginBottom: '16px' }}
                    >
                        <Dragger 
                            name="files" 
                            multiple
                            beforeUpload={() => false}
                            onChange={onDraggerChange}
                            showUploadList={false}
                        >
                            <p className="ant-upload-drag-icon">
                                <FolderOpenOutlined />
                            </p>
                            <p className="ant-upload-text" style={{ fontSize: '14px' }}>点击或拖拽文件夹/多图上传</p>
                        </Dragger>
                    </Form.Item>

                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        {/* 已移除：单张图片上传按钮 */}
                        <Button onClick={() => {
                            if (dirInputRef.current) {
                                dirInputRef.current.click();
                            } else {
                                message.warning('当前浏览器不支持按文件夹选择，请改用多选');
                            }
                        }}>
                            按文件夹添加
                        </Button>
                        <Upload accept=".zip,.rar" multiple={false} showUploadList={false} beforeUpload={beforeUploadArchive}>
                            <Button icon={<UploadOutlined />} loading={archiveUploading}>
                                上传压缩文件（zip/rar）
                            </Button>
                        </Upload>
                        
                        <input
                            type="file"
                            // @ts-ignore: webkitdirectory 非标准属性，实际受支持的浏览器可用
                            webkitdirectory=""
                            directory={true as any}
                            multiple
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={dirInputRef}
                            onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                if (!files.length) return;
                                const added: any[] = [];
                                setItems(prev => {
                                    const existSet = new Set(prev.map(p => p.uid));
                                    const arr = [...prev];
                                    for (const f of files) {
                                        const uid = `${f.name}-${f.size}-${f.lastModified}`;
                                        if (existSet.has(uid)) continue;
                                        const preview = URL.createObjectURL(f);
                                        // 从 webkitRelativePath 推断文件夹名作为默认分类
                                        const rel = (f as any).webkitRelativePath || '';
                                        const folder = rel && rel.includes('/') ? rel.split('/')[0] : '';
                                        arr.push({ uid, file: f, preview, tags: '', category: folder || '' });
                                        added.push({ uid, name: f.name, originFileObj: f });
                                    }
                                    return arr;
                                });
                                if (added.length) {
                                    const curList: any[] = form.getFieldValue('dragger') || [];
                                    form.setFieldsValue({ dragger: [...curList, ...added] });
                                }
                                if (e.target) e.target.value = '';
                            }}
                        />
                        {/* 移除压缩包 input */}
                    </div>
                    {/* 全局描述/可管理标签 应用条（放在“按文件夹添加”按钮下方） */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Input
                                size="small"
                                placeholder="全局描述（一次性覆盖全部）"
                                value={globalBarDescription}
                                onChange={(e) => setGlobalBarDescription(e.target.value)}
                                style={{ maxWidth: 220 }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Input
                                size="small"
                                placeholder="输入标签后回车/添加（可多次添加）"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onPressEnter={() => {
                                    const parts = (newTag || '').split(',').map(s => s.trim()).filter(Boolean);
                                    if (!parts.length) return;
                                    setManagedTags(prev => {
                                        const seen = new Set(prev);
                                        const merged = [...prev];
                                        parts.forEach(p => { if (p && !seen.has(p)) { seen.add(p); merged.push(p); } });
                                        return merged;
                                    });
                                    setNewTag('');
                                }}
                                style={{ maxWidth: 200 }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Button
                                onClick={() => {
                                    const parts = (newTag || '').split(',').map(s => s.trim()).filter(Boolean);
                                    if (!parts.length) return;
                                    setManagedTags(prev => {
                                        const seen = new Set(prev);
                                        const merged = [...prev];
                                        parts.forEach(p => { if (p && !seen.has(p)) { seen.add(p); merged.push(p); } });
                                        return merged;
                                    });
                                    setNewTag('');
                                }}
                            >添加</Button>
                            <Button type="primary" onClick={applyGlobalBarToAll}>应用到全部</Button>
                        </div>
                        {/* 快速标签（与PC一致，点击追加到已选） */}
                        {quickTags.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {quickTags.map(tag => (
                                    <span
                                        key={tag}
                                        onClick={() => setManagedTags(prev => prev.includes(tag) ? prev : [...prev, tag])}
                                        style={{
                                            fontSize: 12,
                                            color: '#4096ff',
                                            background: '#f0f8ff',
                                            border: '1px solid #b3d8ff',
                                            padding: '4px 8px',
                                            borderRadius: 12,
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                        }}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}
                        {/* 已选标签（可调序/删除/设为常用） */}
                        {managedTags.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div style={{ fontSize: 12, color: '#595959', fontWeight: 600 }}>已选标签（可调序/删除/设为常用）：</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {managedTags.map((t, idx) => (
                                        <span key={`${t}-${idx}`} style={{ fontSize: 12, color: '#fff', background: '#4096ff', border: '1px solid #b3d8ff', padding: '4px 8px', borderRadius: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            {t}
                                            <span style={{ display: 'inline-flex', gap: 4 }}>
                                                <ArrowUpOutlined onClick={() => setManagedTags(prev => { if (idx === 0) return prev; const c = prev.slice(); const [x] = c.splice(idx,1); c.splice(idx-1,0,x); return c; })} />
                                                <ArrowDownOutlined onClick={() => setManagedTags(prev => { if (idx === prev.length-1) return prev; const c = prev.slice(); const [x] = c.splice(idx,1); c.splice(idx+1,0,x); return c; })} />
                                                <DeleteOutlined onClick={() => setManagedTags(prev => prev.filter((_, i) => i !== idx))} />
                                                <StarOutlined onClick={() => quickActions.add(t)} />
                                            </span>
                                        </span>
                                    ))}
                                </div>
                                <div>
                                    <Button size="small" type="link" onClick={() => quickActions.addMany(managedTags)}>保存当前为常用</Button>
                                </div>
                            </div>
                        )}
                        {managedTags.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div style={{ fontSize: 12, color: '#595959', fontWeight: 600 }}>已选标签（可调序/删除）：</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {managedTags.map((t, idx) => (
                                        <span key={`${t}-${idx}`} style={{ fontSize: 12, color: '#fff', background: '#4096ff', border: '1px solid #b3d8ff', padding: '4px 8px', borderRadius: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            {t}
                                            <span style={{ display: 'inline-flex', gap: 4 }}>
                                                <ArrowUpOutlined onClick={() => setManagedTags(prev => { if (idx === 0) return prev; const c = prev.slice(); const [x] = c.splice(idx,1); c.splice(idx-1,0,x); return c; })} />
                                                <ArrowDownOutlined onClick={() => setManagedTags(prev => { if (idx === prev.length-1) return prev; const c = prev.slice(); const [x] = c.splice(idx,1); c.splice(idx+1,0,x); return c; })} />
                                                <DeleteOutlined onClick={() => setManagedTags(prev => prev.filter((_, i) => i !== idx))} />
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 已移除：全局信息与全局快速标签区域 */}

                    {items.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <Divider style={{ margin: '8px 0' }} />
                             <div style={{ fontWeight: 500, marginBottom: 8 }}>待上传图片（可添加描述与标签）</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                                {items.map((it) => (
                                    <div key={it.uid}>
                                        <Card
                                            size="small"
                                            bodyStyle={{ padding: 8 }}
                                            cover={<img src={it.preview} alt={it.file.name} style={{ width: '100%', height: 160, objectFit: 'contain', background: '#f5f5f5', borderRadius: 6 }} loading="lazy" decoding="async" />}
                                            actions={[<DeleteOutlined key="del" onClick={() => removeItem(it.uid)} />]}
                                        >
                                            <div style={{ fontSize: 12, marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.file.name}</div>
                                        <Input
                                            size="small"
                                            placeholder="描述（可选，例如：T1234 或文件夹名）"
                                            value={it.category}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                // 同步到当前所有项
                                                setItems(prev => prev.map(x => ({ ...x, category: val })));
                                            }}
                                            style={{ marginBottom: 6 }}
                                        />
                                        <Input
                                            size="small"
                                            placeholder="标签（可选，逗号分隔）"
                                            value={it.tags}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                // 同步到当前所有项
                                                setItems(prev => prev.map(x => ({ ...x, tags: val })));
                                            }}
                                        />
                                            {/* 移除：单项快速标签 */}
                                        </Card>
                                        {/* 保留全局一键应用，移除卡片上的一键按钮 */}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 解压后展示（两列，宽度不超过开始上传按钮容器宽度） */}
                    {archiveResults.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ fontWeight: 500, marginBottom: 8 }}>解压结果（可编辑描述与标签）</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                                {archiveResults.map((it, idx) => (
                                    <Card
                                        key={it.id}
                                        size="small"
                                        bodyStyle={{ padding: 8 }}
                                        cover={<img src={getImageThumbnail(it.id, 300)} alt={it.filename} style={{ width: '100%', height: 160, objectFit: 'contain', background: '#f5f5f5', borderRadius: 6 }} loading="lazy" decoding="async" />}
                                    >
                                        <div style={{ fontSize: 12, marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.filename}</div>
                                        <Input
                                            size="small"
                                            placeholder="描述（可编辑）"
                                            value={it.description}
                                            onChange={(e) => setArchiveResults(prev => prev.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))}
                                            style={{ marginBottom: 6 }}
                                        />
                                        <Input
                                            size="small"
                                            placeholder="标签（逗号分隔）"
                                            value={(it.tags || []).join(',')}
                                            onChange={(e) => setArchiveResults(prev => prev.map((x, i) => i === idx ? { ...x, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } : x))}
                                            onPressEnter={() => { /* 回车不再单独保存，统一由“开始上传”提交 */ }}
                                        />
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block disabled={items.length === 0 && archiveResults.length === 0}>
                            开始上传
                        </Button>
                    </Form.Item>
                </Form>
            </Spin>
            {/* 移除：管理常用标签弹窗 */}
        </div>
    );
};

export default MobileUploadPage;
